import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  Clock3,
  FileBadge2,
  Loader2,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  UserRoundCog,
  Users,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const Organizations = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isEnglish = lang === "en";
  const labels = useMemo(
    () =>
      isEnglish
        ? {
            eyebrow: "Platform administration",
            title: "Organization environments",
            subtitle:
              "Organizations are created through the secure Administrator request workflow. Each workspace has isolated data, its own catalog and one official PDF template.",
            workflowTitle: "Secure creation workflow",
            workflowText:
              "The Super Admin approves an Administrator request. The requester then receives an email to set a password, create the organization and upload its blank PDF certificate template.",
            requests: "Open Administrator requests",
            refresh: "Refresh",
            loading: "Loading organizations...",
            noOrganizations: "No organization found.",
            organizations: "Existing organizations",
            admin: "Administrator",
            noAdmin: "No administrator",
            clients: "Clients",
            participants: "Participants",
            certificates: "Certificates",
            active: "Active",
            inactive: "Inactive",
            modelRule: "One organization PDF template",
            cb4cRule: "CB4C keeps its four existing templates",
          }
        : {
            eyebrow: "Administration de la plateforme",
            title: "Environnements des organisations",
            subtitle:
              "Les organisations sont créées via le parcours sécurisé de demande Administrateur. Chaque espace possède des données isolées, son catalogue et un modèle PDF officiel.",
            workflowTitle: "Parcours de création sécurisé",
            workflowText:
              "Le Super Admin approuve une demande Administrateur. Le demandeur reçoit ensuite un email pour définir son mot de passe, créer son organisation et déposer son modèle PDF vierge.",
            requests: "Ouvrir les demandes Administrateur",
            refresh: "Actualiser",
            loading: "Chargement des organisations...",
            noOrganizations: "Aucune organisation trouvée.",
            organizations: "Organisations existantes",
            admin: "Administrateur",
            noAdmin: "Aucun administrateur",
            clients: "Clients",
            participants: "Participants",
            certificates: "Certificats",
            active: "Active",
            inactive: "Inactive",
            modelRule: "Un modèle PDF propre à l’organisation",
            cb4cRule: "CB4C conserve ses quatre modèles existants",
          },
    [isEnglish]
  );

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const isSuperAdmin =
        user?.type === "Admin" &&
        (user?.isSuperAdmin || user?.adminRole === "SuperAdmin");
      if (!token) navigate("/login", { replace: true });
      else if (!isSuperAdmin) navigate("/unauthorized", { replace: true });
    } catch {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const loadOrganizations = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/organizations`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json().catch(() => []);
      if (!response.ok) throw new Error(data.message || "Unable to load organizations");
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
  }, []);


  return (
    <main className="min-h-screen bg-slate-50 pb-20 text-slate-900">
      <section className="relative overflow-hidden bg-slate-950 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(37,99,235,0.35),transparent_30%),radial-gradient(circle_at_85%_50%,rgba(6,182,212,0.22),transparent_28%)]" />
        <div className="relative mx-auto max-w-7xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-xs font-black uppercase tracking-[0.16em] text-cyan-100">
            <Sparkles className="h-4 w-4" />
            {labels.eyebrow}
          </div>
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-black tracking-tight sm:text-5xl">{labels.title}</h1>
              <p className="mt-4 text-sm leading-7 text-slate-300 sm:text-base">{labels.subtitle}</p>
            </div>
            <Link
              to="/requests/all"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 text-sm font-black text-blue-800 transition hover:bg-blue-50"
            >
              <Send className="h-4 w-4" />
              {labels.requests}
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl space-y-8 px-4 pt-8 sm:px-6 lg:px-8">
        <section className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white">
                <ShieldCheck className="h-6 w-6" />
              </span>
              <div>
                <h2 className="text-xl font-black text-slate-950">{labels.workflowTitle}</h2>
                <p className="mt-2 text-sm leading-7 text-slate-600">{labels.workflowText}</p>
              </div>
            </div>
          </div>
          <div className="rounded-[2rem] bg-slate-950 p-6 text-white shadow-lg">
            <div className="space-y-4 text-sm font-semibold">
              <div className="flex items-center gap-3">
                <FileBadge2 className="h-5 w-5 text-cyan-300" />
                {labels.modelRule}
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                {labels.cb4cRule}
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-black text-slate-950">{labels.organizations}</h2>
              <p className="mt-1 text-sm text-slate-500">{organizations.length} environnement(s)</p>
            </div>
            <button
              type="button"
              onClick={loadOrganizations}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {labels.refresh}
            </button>
          </div>

          {error && (
            <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-64 items-center justify-center rounded-[2rem] border border-slate-200 bg-white">
              <Loader2 className="mr-3 h-5 w-5 animate-spin text-blue-600" />
              <span className="text-sm font-bold text-slate-600">{labels.loading}</span>
            </div>
          ) : organizations.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-10 text-center text-sm font-bold text-slate-500">
              {labels.noOrganizations}
            </div>
          ) : (
            <div className="space-y-4">
              {organizations.map((organization) => (
                <article key={organization._id} className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl">
                  <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white">
                        <Building2 className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-base font-black text-slate-950">{organization.name}</h3>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${organization.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                            {organization.status === "Active" ? <CheckCircle2 className="h-3 w-3" /> : <Clock3 className="h-3 w-3" />}
                            {organization.status === "Active" ? labels.active : labels.inactive}
                          </span>
                        </div>
                        <p className="mt-1 truncate font-mono text-xs text-slate-400">{organization.slug}</p>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm">
                      <p className="text-xs font-black uppercase tracking-wide text-slate-400">{labels.admin}</p>
                      <p className="mt-1 font-black text-slate-900">{organization.primaryAdmin?.name || labels.noAdmin}</p>
                      {organization.primaryAdmin?.email && <p className="mt-1 text-xs text-slate-500">{organization.primaryAdmin.email}</p>}
                    </div>
                  </div>

                  <div className="grid border-t border-slate-100 sm:grid-cols-3">
                    {[
                      [Users, labels.clients, organization.counts?.clients || 0],
                      [UserRoundCog, labels.participants, organization.counts?.participants || 0],
                      [FileBadge2, labels.certificates, organization.counts?.certificates || 0],
                    ].map(([Icon, label, count]) => (
                      <div key={label} className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 last:border-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
                        <Icon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-xs font-bold text-slate-500">{label}</p>
                          <p className="text-lg font-black text-slate-950">{count}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Organizations;
